/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * Persona Agents — admin page (frontend Phase 1).
 *
 * Defensive 501 handling: the page treats `501 + phase:"scaffold"` as a
 * first-class "Coming soon" state. When V2 promotes endpoints from throw
 * NotImplementedError to real callV2(...), this UI receives 200 instead
 * of 501 and seamlessly renders the data — no frontend changes required.
 *
 * Pattern mirrors Research Studio (`ResearchStudio.jsx`):
 *   - AdminLayout shell + back button
 *   - adminApi for HTTP (handles 401 refresh, 2FA, password expiry)
 *   - Inline styles using V1's slate/cyan/purple palette
 *   - Response unwrap: `body?.data ?? body` then dig for entries
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import adminApi from '../../services/adminApi';
import PersonaAgentsList from './components/PersonaAgentsList';
import PersonaAgentDetailDrawer from './components/PersonaAgentDetailDrawer';
import './PersonaAgents.css';

const AUTO_REFRESH_MS = 30000;
const PAGE_SIZE = 25;

const ENTITY_TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'individual', label: 'Individual' },
  { value: 'organization', label: 'Organization' },
  { value: 'university', label: 'University' },
  { value: 'institute', label: 'Institute' },
];

const STATE_OPTIONS = [
  { value: '', label: 'All states' },
  { value: 'draft', label: 'Draft' },
  { value: 'profile_building', label: 'Profile Building' },
  { value: 'profile_review', label: 'Profile Review' },
  { value: 'test_instance', label: 'Test Instance' },
  { value: 'approved', label: 'Approved' },
  { value: 'live', label: 'Live' },
  { value: 'paused', label: 'Paused' },
  { value: 'archived', label: 'Archived' },
];

const RISK_OPTIONS = [
  { value: '', label: 'All risk bands' },
  { value: 'low', label: 'Low (≤20)' },
  { value: 'moderate', label: 'Moderate (21-50)' },
  { value: 'high', label: 'High (51-80)' },
  { value: 'critical', label: 'Critical (≥81)' },
];

// NOTE: The original brief listed a "Workspace" filter, but V2 P4 (commit
// b59d734) settled on `owner_user_id` instead of `workspace_id`. Filtering
// by owner requires a user picker, which lands in Phase 2 alongside the
// real "+ New" flow. Removing the placeholder filter here avoids shipping
// stub UI that misrepresents the schema.

/**
 * Classify an axios error into one of the canonical UI states. Centralized
 * so the same logic is used by the page and by the detail drawer.
 */
function classifyError(err) {
  const status = err?.response?.status;
  const body = err?.response?.data;
  if (status === 501 && body?.phase === 'scaffold') {
    return { kind: 'coming_soon', v2Endpoint: body.v2Endpoint || null };
  }
  if (status === 403) return { kind: 'permission', message: body?.error || 'Permission denied' };
  if (status === 401) return { kind: 'auth', message: 'Session expired' };
  return { kind: 'server', message: body?.error || err?.message || 'Failed to load' };
}

/**
 * Pull the entity array out of whatever envelope V2 uses. Defensive against
 * shape drift — recon §8 flagged "NEVER assume V2 response shape".
 */
function extractEntities(body) {
  if (!body) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.entities)) return body.entities;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.data?.entities)) return body.data.entities;
  if (Array.isArray(body.results)) return body.results;
  return [];
}

function extractPagination(body) {
  const p = body?.pagination || body?.data?.pagination || {};
  return {
    total: p.total ?? null,
    page: p.page ?? 1,
    pageSize: p.page_size ?? p.pageSize ?? PAGE_SIZE,
    totalPages: p.total_pages ?? p.totalPages ?? null,
  };
}

export default function PersonaAgents() {
  const [entities, setEntities] = useState([]);
  const [pagination, setPagination] = useState({
    total: null, page: 1, pageSize: PAGE_SIZE, totalPages: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);            // classified error or null
  const [v2Status, setV2Status] = useState('unknown'); // 'unknown' | 'live' | 'coming_soon'
  const [v2Endpoint, setV2Endpoint] = useState(null);

  const [filters, setFilters] = useState({
    type: '', state: '', risk: '',
  });
  const [page, setPage] = useState(1);

  const [selectedEntityId, setSelectedEntityId] = useState(null);
  const [selectedEntityHint, setSelectedEntityHint] = useState(null);

  const refreshTimer = useRef(null);
  const isMountedRef = useRef(true);

  const fetchEntities = useCallback(async () => {
    setLoading(true);
    setError(null);

    const params = {
      page,
      limit: PAGE_SIZE,
    };
    if (filters.type) params.entity_type = filters.type;
    if (filters.state) params.lifecycle_state = filters.state;
    if (filters.risk) params.risk_band = filters.risk;
    // V2 P4 also filters soft-deleted by default (is_deleted boolean).
    // Surface a toggle in Phase 2; for now rely on V2's default.

    try {
      const res = await adminApi.get('/api/admin/persona-agents', { params });
      const body = res?.data ?? res;
      if (!isMountedRef.current) return;
      setEntities(extractEntities(body));
      setPagination(extractPagination(body));
      setV2Status('live');
      setV2Endpoint(null);
    } catch (e) {
      if (!isMountedRef.current) return;
      const classified = classifyError(e);
      if (classified.kind === 'coming_soon') {
        setV2Status('coming_soon');
        setV2Endpoint(classified.v2Endpoint);
        setEntities([]);
        setPagination({ total: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 1 });
      } else {
        setError(classified);
        setEntities([]);
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [page, filters]);

  // Initial + filter/page change fetch
  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  // Auto-refresh every 30s. Reset timer whenever fetchEntities changes
  // (so a new filter cycle gets a fresh 30s window).
  useEffect(() => {
    if (refreshTimer.current) clearInterval(refreshTimer.current);
    refreshTimer.current = setInterval(() => {
      fetchEntities();
    }, AUTO_REFRESH_MS);
    return () => {
      if (refreshTimer.current) clearInterval(refreshTimer.current);
    };
  }, [fetchEntities]);

  // Cancel pending state updates after unmount.
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const handleFilterChange = (key) => (e) => {
    setFilters((prev) => ({ ...prev, [key]: e.target.value }));
    setPage(1);
  };

  const handleRowClick = (id, entity) => {
    if (!id) return;
    setSelectedEntityId(id);
    setSelectedEntityHint(entity || null);
  };

  const closeDrawer = () => {
    setSelectedEntityId(null);
    setSelectedEntityHint(null);
  };

  const showingFrom = entities.length === 0
    ? 0
    : (pagination.page - 1) * pagination.pageSize + 1;
  const showingTo = entities.length === 0
    ? 0
    : (pagination.page - 1) * pagination.pageSize + entities.length;
  const totalLabel = pagination.total === null ? '?' : pagination.total;
  const totalPagesLabel = pagination.totalPages || 1;

  return (
    <AdminLayout showBackButton backPath="/admin/dashboard">
      <div className="persona-agents-page" style={{
        padding: '24px',
        maxWidth: '1200px',
        margin: '0 auto',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '16px',
          flexWrap: 'wrap',
          marginBottom: '20px',
        }}>
          <div>
            <h1 style={{
              margin: '0 0 4px',
              fontSize: '22px',
              fontWeight: 700,
              color: '#1e293b',
            }}>
              🤖 Persona Agents
            </h1>
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: '#64748b',
            }}>
              Manage AI persona agents across the V2 persona-agent-service.
              {v2Status === 'coming_soon' && (
                <span style={{
                  marginLeft: '8px',
                  fontSize: '11px',
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: '12px',
                  background: '#fef3c7',
                  color: '#92400e',
                }}>
                  Phase 1 — backend scaffold
                </span>
              )}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={fetchEntities}
              disabled={loading}
              style={{
                padding: '8px 14px',
                background: 'white',
                color: '#7c3aed',
                border: '1px solid #7c3aed',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: 500,
              }}
            >
              {loading ? '⏳ Refreshing…' : '↻ Refresh'}
            </button>
            <button
              disabled
              title="Coming soon — V2 endpoint not yet live"
              style={{
                padding: '8px 14px',
                background: '#94a3b8',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'not-allowed',
                fontSize: '13px',
                fontWeight: 500,
                opacity: 0.7,
              }}
            >
              + New
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          marginBottom: '20px',
          padding: '12px 16px',
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
        }}>
          <select
            value={filters.type}
            onChange={handleFilterChange('type')}
            style={selectStyle}
            aria-label="Filter by entity type"
          >
            {ENTITY_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            value={filters.state}
            onChange={handleFilterChange('state')}
            style={selectStyle}
            aria-label="Filter by lifecycle state"
          >
            {STATE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            value={filters.risk}
            onChange={handleFilterChange('risk')}
            style={selectStyle}
            aria-label="Filter by SEAL risk band"
          >
            {RISK_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {/* Owner filter deferred to Phase 2 — needs a user picker (V2 P4
              field is `owner_user_id`, not a workspace string). */}
        </div>

        {/* List */}
        <PersonaAgentsList
          entities={entities}
          loading={loading && entities.length === 0}
          error={error}
          v2Status={v2Status}
          v2Endpoint={v2Endpoint}
          onRowClick={handleRowClick}
          onRetry={fetchEntities}
        />

        {/* Pagination */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          marginTop: '16px',
          padding: '12px 16px',
          color: '#64748b',
          fontSize: '13px',
        }}>
          <span>
            Showing {showingFrom}–{showingTo} of {totalLabel}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page <= 1 || loading}
              style={paginationBtnStyle(pagination.page <= 1 || loading)}
            >
              ‹ Prev
            </button>
            <span>Page {pagination.page} / {totalPagesLabel}</span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={
                loading
                || (pagination.totalPages !== null && pagination.page >= pagination.totalPages)
                || entities.length < pagination.pageSize
              }
              style={paginationBtnStyle(
                loading
                || (pagination.totalPages !== null && pagination.page >= pagination.totalPages)
                || entities.length < pagination.pageSize
              )}
            >
              Next ›
            </button>
          </div>
        </div>

        {/* Detail drawer */}
        <PersonaAgentDetailDrawer
          entityId={selectedEntityId}
          entityHint={selectedEntityHint}
          isOpen={Boolean(selectedEntityId)}
          onClose={closeDrawer}
        />
      </div>
    </AdminLayout>
  );
}

const selectStyle = {
  padding: '8px 12px',
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
  fontSize: '13px',
  color: '#1e293b',
  background: 'white',
  minWidth: '140px',
};

function paginationBtnStyle(disabled) {
  return {
    padding: '6px 12px',
    background: 'white',
    color: disabled ? '#cbd5e1' : '#7c3aed',
    border: `1px solid ${disabled ? '#e2e8f0' : '#7c3aed'}`,
    borderRadius: '6px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '12px',
    fontWeight: 500,
  };
}
